/**
 * HealthPermissionCard — UI surface for granting HealthKit / Health Connect
 * access. Anti-shame copy: we tell the user exactly what we read, exactly
 * what we write, and that they can revoke it any time. We never imply they
 * "have to" connect — Enferno works fully without it.
 *
 * Renders three states:
 *   1. Native, not yet asked       → big primary CTA + bullet list of asks
 *   2. Native, granted (or asked)  → small status line + last sync
 *   3. Web (no native shell)       → muted explainer + "available on iOS/Android"
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ShieldCheck, RefreshCw, AlertCircle, Smartphone, Upload, Lock, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTierState } from '../hooks/useTierState';
import {
  isHealthAvailable,
  requestHealthPermissions,
  foregroundSyncWindow,
  collectBodyMetricsSnapshot,
} from '../lib/healthService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HealthPermissionCardLocked = () => (
  <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="health-card-locked">
    <div className="flex items-start gap-3 mb-3">
      <div className="w-9 h-9 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
        <Lock className="w-4 h-4 text-enf-accent-fg" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-accent-fg mb-1">Enferno feature</div>
        <h3 className="font-display text-lg tracking-wide">Wearable sync</h3>
      </div>
    </div>
    <p className="text-sm text-enf-text-secondary leading-relaxed mb-4">
      Apple Health and Google Fit sync push every workout, ride, and martial-arts session to your wearables (Garmin, Whoop, Oura, Fitbit). Available on Enferno.
    </p>
    <Link
      to="/pricing"
      className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
      data-testid="health-card-paywall-cta"
    >
      See pricing <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

const HealthPermissionCardInner = ({ onSynced }) => {
  const { token } = useAuth();
  const { profile, update } = useProfile();
  const native = isHealthAvailable();  const [status, setStatus] = useState('idle'); // idle | asking | syncing | ready | error
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState('');
  const [counts, setCounts] = useState(null);

  const handleConnect = async () => {
    if (!native) return;
    setStatus('asking');
    setError('');
    const perm = await requestHealthPermissions();
    if (!perm.granted) {
      setStatus('error');
      setError(perm.reason || 'Permission was not granted.');
      return;
    }
    setStatus('syncing');
    const sync = await foregroundSyncWindow();
    if (sync.workouts.length === 0) {
      setStatus('ready');
      setLastSync(sync.ranAt);
      setCounts({ inserted: 0, skipped_duplicate: 0, total_seen: 0 });
      onSynced?.(sync);
      return;
    }
    // POST workouts + body-metrics to backend with dedup-safe payload.
    try {
      const payload = {
        workouts: sync.workouts.map((w) => ({
          raw_healthkit_id: w.uuid || w.id || `${w.startDate}-${w.workoutActivityType}`,
          source_name: w.sourceName || null,
          activity_type: w.workoutActivityType || null,
          start_date: w.startDate,
          end_date: w.endDate,
          duration_seconds: w.duration ?? null,
          distance_meters: w.distance ?? null,
          active_energy_kcal: w.calories ?? null,
          average_heart_rate: w.averageHeartRate ?? null,
          metadata: w.metadata || null,
        })),
        sync_ran_at: sync.ranAt,
      };
      const res = await axios.post(`${API}/health/workouts/sync`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // v2: also push today's body-metric snapshot for the Pacing engine.
      // Fire-and-forget: workout sync is the primary contract; metrics are
      // a bonus that shouldn't break the UI if they fail.
      try {
        const snapshot = await collectBodyMetricsSnapshot();
        if (snapshot) {
          await axios.post(
            `${API}/health/metrics/sync`,
            { snapshots: [snapshot], sync_ran_at: sync.ranAt },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
      } catch { /* non-fatal */ }
      setCounts(res.data);
      setStatus('ready');
      setLastSync(sync.ranAt);
      onSynced?.({ ...sync, server: res.data });
    } catch (e) {
      setStatus('error');
      setError('Could not save to your account. Try again in a moment.');
    }
  };

  // --- Web state (not in a native shell) ---
  if (!native) {
    return (
      <div className="p-5 bg-enf-surface-deep border border-enf-surface-border rounded-xl" data-testid="health-card-web">
        <div className="flex items-center gap-3 mb-2">
          <Smartphone className="w-5 h-5 text-enf-role-info" />
          <h3 className="font-display text-lg tracking-wide">Health sync</h3>
        </div>
        <p className="text-sm text-enf-text-secondary leading-relaxed">
          Apple Health and Google Health Connect sync run in the Enferno mobile app. Install it from the App Store or Play Store when you're ready — your account here will pick up right where it left off.
        </p>
      </div>
    );
  }

  // --- Native, connected ---
  if (status === 'ready' || lastSync) {
    const writeBackOn = !!profile.healthkitWriteBackEnabled;
    return (
      <div className="p-5 bg-enf-surface-deep border border-enf-role-info/30 rounded-xl" data-testid="health-card-ready">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-5 h-5 text-enf-role-info" />
          <h3 className="font-display text-lg tracking-wide">Health sync active</h3>
        </div>
        {counts && (
          <p className="text-sm text-enf-text-secondary leading-relaxed mb-3">
            Last sync brought in <span className="text-enf-text-primary font-semibold">{counts.inserted}</span> new {counts.inserted === 1 ? 'workout' : 'workouts'}
            {counts.skipped_duplicate > 0 && (
              <> · {counts.skipped_duplicate} already on file</>
            )}
            .
          </p>
        )}

        {/* Write-back toggle — global, default OFF, single switch the user owns. */}
        <label
          className="flex items-start gap-3 p-3 mb-3 bg-enf-surface-mid/50 border border-enf-surface-border rounded-md cursor-pointer hover:bg-enf-surface-mid transition-colors"
          data-testid="health-writeback-toggle-row"
        >
          <input
            type="checkbox"
            checked={writeBackOn}
            onChange={(e) => update({ healthkitWriteBackEnabled: e.target.checked })}
            className="mt-0.5 w-4 h-4 accent-enf-accent"
            data-testid="health-writeback-toggle"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-enf-text-primary">
              <Upload className="w-3.5 h-3.5 text-enf-role-info" />
              Save Enferno workouts to Apple Health
            </div>
            <p className="text-xs text-enf-text-muted mt-1 leading-relaxed">
              When on, every workout you log here is saved to Apple Health and shared with any wearables you've paired (Garmin, Whoop, Oura, etc.). Off by default.
            </p>
          </div>
        </label>

        <button
          onClick={handleConnect}
          disabled={status === 'syncing'}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-xs font-semibold text-enf-text-secondary transition-colors disabled:opacity-50"
          data-testid="health-resync-button"
        >
          <RefreshCw className={`w-3 h-3 ${status === 'syncing' ? 'animate-spin' : ''}`} />
          {status === 'syncing' ? 'Syncing…' : 'Sync again'}
        </button>
      </div>
    );
  }

  // --- Native, not yet asked / error ---
  return (
    <div className="p-5 bg-enf-surface-deep border border-enf-surface-border rounded-xl" data-testid="health-card-prompt">
      <div className="flex items-center gap-3 mb-2">
        <Activity className="w-5 h-5 text-enf-accent-fg" />
        <h3 className="font-display text-lg tracking-wide">Connect Apple Health</h3>
      </div>
      <p className="text-sm text-enf-text-secondary leading-relaxed mb-3">
        Optional. When you connect, today's training respects what your body has already done — workouts, heart rate, sleep, recovery.
      </p>
      <ul className="text-xs text-enf-text-muted space-y-1 mb-4 list-none">
        <li className="flex gap-2"><span className="text-enf-role-info">·</span> We <span className="text-enf-text-secondary">read</span> workouts, heart rate, sleep, body metrics</li>
        <li className="flex gap-2"><span className="text-enf-role-info">·</span> We <span className="text-enf-text-secondary">write back</span> the workouts you log here so wearables stay current</li>
        <li className="flex gap-2"><span className="text-enf-role-info">·</span> You revoke any time in iOS Settings → Health</li>
      </ul>
      {error && (
        <div className="mb-3 px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 text-enf-accent-fg text-xs rounded-md flex items-center gap-2">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
      <button
        onClick={handleConnect}
        disabled={status === 'asking' || status === 'syncing'}
        className="inline-flex items-center gap-2 px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
        data-testid="health-connect-button"
      >
        <Activity className="w-4 h-4" />
        {status === 'asking' ? 'Asking…' : status === 'syncing' ? 'Syncing…' : 'Connect Apple Health'}
      </button>
    </div>
  );
};

// G8: Public export — gates entry on tier state. Free users see the
// "Wearable sync — Enferno feature" lock card; paid users get the real
// HealthPermissionCardInner with connect / sync / progress UI.
const HealthPermissionCard = (props) => {
  const { is_paid: isPaid, loading: tierLoading } = useTierState();
  if (tierLoading) return null;
  if (!isPaid) return <HealthPermissionCardLocked />;
  return <HealthPermissionCardInner {...props} />;
};

export default HealthPermissionCard;
